import { ModelEnsSelector } from "@/components/organisms/ModelEnsSelector/ModelEnsSelector";
import { formatRunInitDateFull } from "@/energyPlotView/EnergyPlotModelSelector/EnergyPlotModelSelector.utils";
import { type EnergyPlotGroup, normalizeEnergyPlotGroupRuns } from "@/energyPlotView/types";
import { resolvePreferredEnergyModel } from "@/energyPlotView/utils";
import { useTimezone } from "@/models/time-control/timeStateHooks";
import {
  type AvailableValueNode,
  useAvailableEnsMembers,
} from "@/overlay/components/LayerStackWindow/EnergyPlotSettings/useAvailableEnergyForecastDataTree";
import type { PlotSerieStyle } from "@/types/data";
import { classNames } from "@/utility/jsx";
import { t } from "@lingui/core/macro";
import { memo, useCallback, useEffect, useMemo } from "react";
import { RunSection } from "./RunSection";
import { Section } from "./Section";
import { SerieOptions } from "./SerieOptions";

interface GroupSettingProps {
  groupIndex: number;
  energyPlotGroup: EnergyPlotGroup;
  forecastType: string;
  updateEnergyGroup: (groupIndex: number, energyGroup: EnergyPlotGroup) => void;
  removeGroup?: (groupIndex: number) => void;
  availableModelNodes: AvailableValueNode[];
}

export const EnergyGroupSettings = memo(function EnergyGroupSettings({
  groupIndex,
  energyPlotGroup,
  removeGroup,
  updateEnergyGroup,
  forecastType,
  availableModelNodes,
}: GroupSettingProps) {
  const timezone = useTimezone();
  const availableModelIds = useMemo(
    () => availableModelNodes.map((node) => node.value).filter((value): value is string => !!value),
    [availableModelNodes],
  );
  const resolvedModel = useMemo(() => {
    return resolvePreferredEnergyModel(availableModelIds, energyPlotGroup.model);
  }, [availableModelIds, energyPlotGroup.model]);

  useEffect(() => {
    if (resolvedModel !== energyPlotGroup.model) {
      updateEnergyGroup(groupIndex, {
        ...energyPlotGroup,
        model: resolvedModel,
      });
    }
  }, [groupIndex, resolvedModel, energyPlotGroup, updateEnergyGroup]);

  const modelItems = useMemo(
    () =>
      availableModelNodes.map((node) => ({
        id: node.value || "",
        title: node.value || "",
      })),
    [availableModelNodes],
  );
  const activeModelId = useMemo(() => (resolvedModel ? [resolvedModel] : []), [resolvedModel]);
  const { ensMembers, ensQuantiles } = useAvailableEnsMembers(forecastType, resolvedModel || "");
  const handleModelSelection = useCallback(
    (selectedItems: { id: string }[]) => {
      const selectedItem = selectedItems[0];

      if (!selectedItem?.id || resolvedModel === selectedItem.id) {
        return;
      }

      updateEnergyGroup(groupIndex, {
        ...energyPlotGroup,
        model: selectedItem.id,
        ensMembers: [],
      });
    },
    [groupIndex, resolvedModel, energyPlotGroup, updateEnergyGroup],
  );

  const handleEnsMemberSelect = useCallback(
    (selectedValues: string[]) => {
      updateEnergyGroup(groupIndex, {
        ...energyPlotGroup,
        ensMembers: selectedValues,
      });
    },
    [groupIndex, energyPlotGroup, updateEnergyGroup],
  );

  const handleRemove = useCallback(() => {
    removeGroup?.(groupIndex);
  }, [groupIndex, removeGroup]);

  const handleUpdateSerieOptions = useCallback(
    (serieName: string, serieOption: PlotSerieStyle) => {
      const actualRun =
        serieName === "power" && energyPlotGroup.actualRun
          ? {
              ...energyPlotGroup.actualRun,
              plotSeriesStyles: {
                ...energyPlotGroup.actualRun.plotSeriesStyles,
                power: serieOption,
              },
            }
          : energyPlotGroup.actualRun;

      updateEnergyGroup(groupIndex, {
        ...energyPlotGroup,
        actualRun,
        plotSeriesStyles: {
          ...energyPlotGroup.plotSeriesStyles,
          [serieName]: serieOption,
        },
      });
    },
    [groupIndex, energyPlotGroup, updateEnergyGroup],
  );
  const normalizedEnergyPlotGroup = useMemo(() => normalizeEnergyPlotGroupRuns(energyPlotGroup), [energyPlotGroup]);
  const runSections = useMemo(() => {
    return (normalizedEnergyPlotGroup.previousRuns ?? []).map((previousRun) => ({
      deltaStyle: previousRun.plotSeriesStyles.combined_delta_1h,
      key: `previous-run-${previousRun.initDate}`,
      previousRun,
      runStyle: previousRun.plotSeriesStyles.power,
      title: formatRunInitDateFull(previousRun.initDate, timezone),
    }));
  }, [normalizedEnergyPlotGroup.previousRuns, timezone]);
  const arePreviousRunsVisible = runSections.some(
    ({ previousRun }) => previousRun.showRun !== false || previousRun.showDelta !== false,
  );
  const handleUpdatePreviousRun = useCallback(
    (initDate: number, serieName: "power" | "combined_delta_1h", serieOption: PlotSerieStyle) => {
      updateEnergyGroup(groupIndex, {
        ...energyPlotGroup,
        actualRun: normalizedEnergyPlotGroup.actualRun,
        previousRuns: (normalizedEnergyPlotGroup.previousRuns ?? []).map((previousRun) =>
          previousRun.initDate === initDate
            ? {
                ...previousRun,
                plotSeriesStyles: {
                  ...previousRun.plotSeriesStyles,
                  [serieName]: serieOption,
                },
              }
            : previousRun,
        ),
      });
    },
    [groupIndex, energyPlotGroup, normalizedEnergyPlotGroup, updateEnergyGroup],
  );
  const handleTogglePreviousRuns = useCallback(() => {
    const visible = !arePreviousRunsVisible;
    updateEnergyGroup(groupIndex, {
      ...energyPlotGroup,
      actualRun: normalizedEnergyPlotGroup.actualRun,
      previousRuns: (normalizedEnergyPlotGroup.previousRuns ?? []).map((previousRun) => ({
        ...previousRun,
        showRun: visible,
        showDelta: visible,
      })),
    });
  }, [groupIndex, energyPlotGroup, arePreviousRunsVisible, normalizedEnergyPlotGroup, updateEnergyGroup]);

  return (
    <>
      <Section
        tag={resolvedModel}
        color={energyPlotGroup.plotSeriesStyles.power?.color}
        onRemove={removeGroup ? handleRemove : undefined}
      >
        <div
          className={classNames(
            "expansion-panel box-section--breakout actionable",
            true && "expansion-panel--expanded",
          )}
        >
          <ModelEnsSelector
            ensOptions={{ members: ensMembers, quantiles: ensQuantiles }}
            modelOptions={modelItems}
            selectedModel={activeModelId[0] || undefined}
            selectedEnsMembers={energyPlotGroup.ensMembers || []}
            onModelChange={(selectedValue) => handleModelSelection([{ id: selectedValue }])}
            onEnsMembersChange={handleEnsMemberSelect}
          />
          <>
            <SerieOptions
              serieOptions={energyPlotGroup.plotSeriesStyles.power}
              onChange={(serieOption) => handleUpdateSerieOptions("power", serieOption)}
            />
            {runSections.length > 0 && (
              <Section
                title={t`Previous runs`}
                subSection
                onHide={handleTogglePreviousRuns}
                isVisible={arePreviousRunsVisible}
              >
                {runSections.map(({ deltaStyle, key, previousRun, runStyle, title }) => (
                  <RunSection
                    key={key}
                    title={title}
                    runStyle={runStyle}
                    deltaStyle={deltaStyle}
                    onRunChange={(serieOption) => handleUpdatePreviousRun(previousRun.initDate, "power", serieOption)}
                    onDeltaChange={(serieOption) =>
                      handleUpdatePreviousRun(previousRun.initDate, "combined_delta_1h", serieOption)
                    }
                  />
                ))}
              </Section>
            )}
          </>
          {/*)}*/}
        </div>
      </Section>
    </>
  );
});
